/**
 * The few pieces both pages need identically. Ported from the #5 prototype.
 *
 * One change from the prototype: the similarity chip no longer colours itself against a
 * threshold. #9 measured that no cut-off separates versions whose creditability rules
 * changed from those whose didn't -- a version at 0.9961 can still forbid what was
 * previously credited -- so colouring by a cut-off would draw a boundary on screen that
 * does not exist. It is now a continuous shade meaning only "more changed" / "less
 * changed", with no point where the colour flips meaning.
 */
import React from 'react';
import { Box, Button, Chip, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

/** Observed range across the corpus is roughly 0.75-1.00; clamp so the ramp is legible. */
export const similarityShade = (value: number, alpha = 0.3): string => {
  const scaled = Math.min(Math.max((value - 0.75) / 0.25, 0), 1);
  return `rgba(120, 120, 120, ${(alpha * (1 - scaled)).toFixed(3)})`;
};

export const Similarity = ({
  cosine,
  size = 'small',
}: {
  cosine: number | undefined;
  size?: 'small' | 'medium';
}) => {
  if (cosine === undefined) {
    return (
      <Tooltip title="No cached embedding for one of these versions, so no similarity. It is computed when a criterion is opened; a failure leaves this blank rather than breaking the page.">
        <Chip size={size} label="—" variant="outlined" />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Cosine similarity of the two rubrics. Triage ordering only — it never decides eligibility, because no cut-off separates equivalent from revised on this corpus.">
      <Chip
        size={size}
        label={cosine.toFixed(4)}
        variant="outlined"
        sx={{
          fontFamily: 'monospace',
          fontWeight: 600,
          backgroundColor: similarityShade(cosine),
        }}
      />
    </Tooltip>
  );
};

/**
 * "Equivalent" / "Different" is the admin's mental model while reading two rubrics; the
 * stored column is `eligible`. Both words are kept deliberately -- see CONTEXT.md.
 */
export const VerdictButtons = ({
  eligible,
  decided,
  disabled,
  disabledReason,
  onVerdict,
  size = 'small',
}: {
  eligible: boolean;
  /** Whether a human has actually ruled, as opposed to inheriting the default. */
  decided: boolean;
  /** Blocks both buttons — busy, not judgeable, blank rubric. */
  disabled?: boolean;
  disabledReason?: string;
  onVerdict: (eligible: boolean) => void;
  size?: 'small' | 'large';
}) => {
  // The button matching the recorded decision is disabled: re-applying it is a no-op, and
  // greying it is how the page shows what was already decided. The opposite button stays
  // live so the decision can always be reversed.
  //
  // This survives a reload because `decided` and `eligible` come from
  // policy_version_eligibility via the API, not from local state — there is nothing here
  // that resets when the page is refetched.
  const alreadyEquivalent = decided && eligible;
  const alreadyDifferent = decided && !eligible;

  const button = (
    verdict: boolean,
    label: string,
    colour: 'success' | 'error',
    icon: React.ReactNode,
    alreadyChosen: boolean
  ) => {
    const isDisabled = Boolean(disabled) || alreadyChosen;
    const reason = disabled
      ? disabledReason
      : alreadyChosen
        ? `Already marked ${label.toLowerCase()}. Choose ${
            verdict ? 'Different' : 'Equivalent'
          } to reverse it.`
        : undefined;

    const element = (
      <Button
        size={size === 'large' ? 'large' : 'small'}
        variant={alreadyChosen ? 'contained' : 'outlined'}
        color={colour}
        startIcon={icon}
        disabled={isDisabled}
        onClick={() => onVerdict(verdict)}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
      >
        {label}
      </Button>
    );

    // A disabled button emits no pointer events, so a tooltip needs a wrapper to hang off.
    return reason ? (
      <Tooltip title={reason}>
        <span>{element}</span>
      </Tooltip>
    ) : (
      element
    );
  };

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {button(true, 'Equivalent', 'success', <CheckIcon />, alreadyEquivalent)}
      {button(false, 'Different', 'error', <CloseIcon />, alreadyDifferent)}
    </Box>
  );
};

export const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB') : '—';

/**
 * Copy text to the clipboard, reporting failure rather than swallowing it.
 *
 * `navigator.clipboard` is undefined outside a secure context — plain http on anything
 * other than localhost — so this can genuinely be unavailable rather than merely fail.
 * The caller surfaces the outcome, because a copy button that silently does nothing is
 * worse than one that says it could not.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (!navigator.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};
